import React from "react";
import { useT, useI18n } from "@/i18n";
import ContactForm from "@/components/ContactForm";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, Layers, LineChart, Notebook, BellRing,
  Archive, ScanSearch, ShieldCheck, Compass, Sparkles,
  MessageSquare, ChevronDown
} from "lucide-react";

const IMG_HERO = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80";
const IMG_ABOUT = "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?auto=format&fit=crop&w=1200&q=80";
const IMG_BENEFITS = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80";

export default function Home() {
  const t = useT();
  const { lang } = useI18n();
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <RadarBackdrop />
        <div className="mx-auto max-w-[1240px] px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9F14B] shadow-[0_0_10px_#C9F14B]" />
                {t("hero.eyebrow")}
              </div>
              <h1
                className="mt-6 text-[40px] leading-[1.02] md:text-[68px] lg:text-[80px] font-semibold tracking-[-0.02em] text-[#F1ECDE]"
                data-testid="hero-title"
              >
                {t("hero.title")}
              </h1>
              <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed text-[#c8cdd6] max-w-xl">
                {t("hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C9F14B] text-[#0B0F14] px-6 py-3.5 text-sm font-medium hover:bg-[#d7ff5f] transition-colors"
                >
                  {t("cta.secondary")}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </a>
                <a
                  href="#how"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/10 text-[#F1ECDE] px-6 py-3.5 text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  {t("cta.tertiary")}
                  <ArrowDown size={16} />
                </a>
              </div>

              <dl className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/5 pt-8">
                <Stat value={t("hero.metricAValue")} label={t("hero.metricA")} />
                <Stat value={t("hero.metricBValue")} label={t("hero.metricB")} />
                <Stat value={t("hero.metricCValue")} label={t("hero.metricC")} />
              </dl>
            </div>
            <div className="lg:col-span-5">
              <HeroCard t={t} img={IMG_HERO} />
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="relative py-24 md:py-32">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8">
          <SectionEyebrow>{t("about.eyebrow")}</SectionEyebrow>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE]" data-testid="about-title">
                {t("about.title")}
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[#c8cdd6] max-w-2xl">{t("about.body")}</p>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Pillar icon={<ScanSearch size={18} />} title={t("about.pillarOneTitle")} body={t("about.pillarOneBody")} />
                <Pillar icon={<Layers size={18} />} title={t("about.pillarTwoTitle")} body={t("about.pillarTwoBody")} />
                <Pillar icon={<Compass size={18} />} title={t("about.pillarThreeTitle")} body={t("about.pillarThreeBody")} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                <img
                  src={IMG_ABOUT}
                  alt={t("a11y.aboutImageAlt")}
                  className="w-full h-[440px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F14]/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-[#0B0F14]/80 backdrop-blur border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9F14B]">{t("hero.aboutOverlayEyebrow")}</div>
                  <div className="mt-1 text-sm text-[#F1ECDE]">{t("hero.aboutOverlayTitle")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section className="relative py-24 md:py-28 border-t border-white/5 bg-gradient-to-b from-[#0d131b] to-[#0B0F14]">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8">
          <SectionEyebrow>{t("platform.eyebrow")}</SectionEyebrow>
          <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE] max-w-3xl">
            {t("platform.title")}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#c8cdd6] max-w-2xl">{t("platform.body")}</p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard num="01" icon={<LineChart size={18} />} title={t("platform.itemOneTitle")} body={t("platform.itemOneBody")} />
            <FeatureCard num="02" icon={<Notebook size={18} />} title={t("platform.itemTwoTitle")} body={t("platform.itemTwoBody")} />
            <FeatureCard num="03" icon={<BellRing size={18} />} title={t("platform.itemThreeTitle")} body={t("platform.itemThreeBody")} />
            <FeatureCard num="04" icon={<Archive size={18} />} title={t("platform.itemFourTitle")} body={t("platform.itemFourBody")} />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative py-24 md:py-32">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8">
          <SectionEyebrow>{t("howItWorks.eyebrow")}</SectionEyebrow>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE] max-w-2xl" data-testid="how-title">
              {t("howItWorks.title")}
            </h2>
            <p className="text-[15px] leading-relaxed text-[#c8cdd6] max-w-md">{t("howItWorks.body")}</p>
          </div>

          <ol className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            <StepCard kicker={t("howItWorks.stepOneKicker")} title={t("howItWorks.stepOneTitle")} body={t("howItWorks.stepOneBody")} />
            <StepCard kicker={t("howItWorks.stepTwoKicker")} title={t("howItWorks.stepTwoTitle")} body={t("howItWorks.stepTwoBody")} />
            <StepCard kicker={t("howItWorks.stepThreeKicker")} title={t("howItWorks.stepThreeTitle")} body={t("howItWorks.stepThreeBody")} />
          </ol>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="relative py-24 md:py-32 border-t border-white/5 bg-[#0d131b]">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img
                src={IMG_BENEFITS}
                alt={t("a11y.benefitsImageAlt")}
                className="w-full h-[440px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-transparent to-[#0B0F14]/70" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionEyebrow>{t("benefits.eyebrow")}</SectionEyebrow>
            <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE]" data-testid="benefits-title">
              {t("benefits.title")}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[#c8cdd6] max-w-xl">{t("benefits.body")}</p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              {["itemOne","itemTwo","itemThree","itemFour","itemFive","itemSix"].map((k) => (
                <li key={k} className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
                  <span className="mt-0.5 inline-flex w-6 h-6 items-center justify-center rounded-full bg-[#C9F14B]/15 text-[#C9F14B]">
                    <ShieldCheck size={14} />
                  </span>
                  <span className="text-sm text-[#e6e1d4]">{t(`benefits.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DISCLAIMER + FORM */}
      <section id="contact" className="relative py-24 md:py-32">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <SectionEyebrow>{t("form.eyebrow")}</SectionEyebrow>
            <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE]" data-testid="contact-title">
              {t("disclaimer.title")}
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[#c8cdd6] max-w-xl">{t("disclaimer.body")}</p>
            <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-5 max-w-xl">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-[#C9F14B] mt-0.5" />
                <div className="text-sm text-[#c8cdd6]">
                  <div className="text-[#F1ECDE] font-medium">{t("form.title")}</div>
                  <div className="mt-1">{t("form.body")}</div>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <a
                href={`mailto:${t("footer.legal.email")}`}
                data-testid="mailto-link"
                className="inline-flex items-center gap-2 text-sm text-[#C9F14B] hover:underline underline-offset-4"
              >
                <MessageSquare size={14} /> {t("cta.emailUs")} · {t("footer.legal.email")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-white/10 bg-[#0f141c] p-6 md:p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-24 md:py-32 border-t border-white/5 bg-[#0d131b]">
        <div className="mx-auto max-w-[900px] px-5 md:px-8">
          <SectionEyebrow>{t("faq.eyebrow")}</SectionEyebrow>
          <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#F1ECDE] text-center" data-testid="faq-title">
            {t("faq.title")}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#c8cdd6] text-center max-w-2xl mx-auto">{t("faq.body")}</p>
          <div className="mt-12 space-y-3">
            {(t("faq.items") || []).map((item, i) => (
              <FaqItem key={i} testId={`faq-${i}`} q={item.q} a={item.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* POLICIES LINKS */}
      <section className="relative py-16 border-t border-white/5">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link data-testid="policies-privacy" to={`/${lang}/privacy`} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">{t("policies.eyebrow")}</div>
              <div className="mt-2 text-lg text-[#F1ECDE] font-medium flex items-center justify-between">
                {t("policies.privacyLink")}
                <ArrowRight size={16} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
            <Link data-testid="policies-cookies" to={`/${lang}/cookies`} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">{t("policies.eyebrow")}</div>
              <div className="mt-2 text-lg text-[#F1ECDE] font-medium flex items-center justify-between">
                {t("policies.cookiesLink")}
                <ArrowRight size={16} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
            <Link data-testid="policies-terms" to={`/${lang}/terms`} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">{t("policies.eyebrow")}</div>
              <div className="mt-2 text-lg text-[#F1ECDE] font-medium flex items-center justify-between">
                {t("policies.termsLink")}
                <ArrowRight size={16} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.22em] text-[#8f96a3]">{label}</dt>
      <dd className="mt-1 text-2xl md:text-3xl font-semibold text-[#F1ECDE]">{value}</dd>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">
      <span className="w-6 h-px bg-[#C9F14B]/70" />
      {children}
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#C9F14B]/12 text-[#C9F14B]">{icon}</div>
      <div className="mt-4 text-[15px] font-medium text-[#F1ECDE]">{title}</div>
      <div className="mt-2 text-sm text-[#c8cdd6] leading-relaxed">{body}</div>
    </div>
  );
}

function FeatureCard({ num, icon, title, body }) {
  return (
    <div className="group relative rounded-2xl border border-white/10 bg-[#0B0F14] p-6 overflow-hidden">
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#C9F14B]/10 blur-2xl transition-opacity opacity-0 group-hover:opacity-100" />
      <div className="flex items-center justify-between">
        <div className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[#C9F14B]/12 text-[#C9F14B]">{icon}</div>
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#8f96a3]">{num}</span>
      </div>
      <div className="mt-6 text-lg font-medium text-[#F1ECDE]">{title}</div>
      <div className="mt-2 text-sm text-[#c8cdd6] leading-relaxed max-w-md">{body}</div>
    </div>
  );
}

function StepCard({ kicker, title, body }) {
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9F14B]">{kicker}</div>
      <div className="mt-4 text-lg font-medium text-[#F1ECDE]">{title}</div>
      <div className="mt-2 text-sm text-[#c8cdd6] leading-relaxed">{body}</div>
    </li>
  );
}

function FaqItem({ q, a, defaultOpen, testId }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testId}>
      <button
        type="button"
        className="w-full flex items-center justify-between gap-6 px-5 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-[15px] text-[#F1ECDE] font-medium">{q}</span>
        <ChevronDown
          size={16}
          className={`text-[#c8cdd6] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[#c8cdd6] leading-relaxed border-t border-white/5">{a}</div>
      )}
    </div>
  );
}

function HeroCard({ t, img }) {
  return (
    <div className="relative w-full max-w-[520px] ml-auto">
      <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-br from-[#C9F14B]/10 via-transparent to-transparent blur-2xl" aria-hidden />
      <div className="relative rounded-[24px] border border-white/10 bg-[#0f141c] p-4 shadow-2xl">
        <div className="relative overflow-hidden rounded-[16px] border border-white/5">
          <img src={img} alt={t("a11y.heroImageAlt")} className="w-full h-[280px] md:h-[320px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/80 to-transparent" />
          <div className="absolute inset-x-4 bottom-4 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9F14B]">Zenithradiar</div>
              <div className="mt-1 text-sm text-[#F1ECDE]">{t("hero.cardTitle")}</div>
            </div>
            <div className="rounded-full bg-[#0B0F14]/70 backdrop-blur border border-white/10 px-2.5 py-1 text-[10px] text-[#c8cdd6]">{t("hero.cardVersion")}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <MiniStat label={t("hero.miniSourcesLabel")} value={t("hero.miniSourcesValue")} />
          <MiniStat label={t("hero.miniPanelsLabel")} value={t("hero.miniPanelsValue")} />
          <MiniStat label={t("hero.miniLangsLabel")} value={t("hero.miniLangsValue")} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#8f96a3]">{label}</div>
      <div className="mt-0.5 text-sm text-[#F1ECDE] font-medium">{value}</div>
    </div>
  );
}

function RadarBackdrop() {
  return (
    <>
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1200px 500px at 15% 10%, rgba(201,241,75,0.10), transparent 55%), radial-gradient(900px 500px at 85% 30%, rgba(80,130,255,0.10), transparent 55%), linear-gradient(180deg,#0B0F14 0%, #0B0F14 100%)",
        }}
      />
      <svg
        className="absolute right-[-160px] top-[-80px] -z-10 opacity-40 hidden md:block"
        width="820" height="820" viewBox="0 0 820 820" fill="none" aria-hidden
      >
        <g stroke="#C9F14B" strokeOpacity="0.14">
          <circle cx="410" cy="410" r="120" />
          <circle cx="410" cy="410" r="200" />
          <circle cx="410" cy="410" r="280" />
          <circle cx="410" cy="410" r="360" />
        </g>
        <path d="M410 410 L720 200" stroke="#C9F14B" strokeOpacity="0.35" strokeWidth="1.2" />
        <circle cx="720" cy="200" r="3" fill="#C9F14B" />
      </svg>
    </>
  );
}
